/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Constants
import { queryKeys } from '@config/constants/query-keys';

// Types
import { TApiError } from '@global-types/api/api';
import { TApiResponseDeleteProducer } from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const remove = (
	id: string
): Promise<TApiResponseDeleteProducer> => {
	return apiClient.delete(`/producers/${id} `);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useRemove = (page: number, size: number) => {
	const { t } = useTranslation();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'producers.notifications.producerSuccessfullyDeleted'
				),
				color: 'success',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.producers.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});
};
